
import React, { useState, useEffect, useCallback } from "react";

import Sidebar from "../components/Sidebar";
import POSView from "../components/POSView";
import DirectPOSView from "../components/DirectPOSView";
import MenuManagement from "../components/MenuManagement";
import OrdersView from "../components/OrdersView";
import OrderHistory from "../components/Order-History";
import Categories from "../components/Categories";
import RegisterUser from "../components/RegisterUser";
import AnalyticsDashboard from "./AnalyticsDashboard";
import ChefOrdersView from "../components/ChefOrdersView";
import Profile from "./Profile";
import PartyView from "../components/PartyView";
import ExpensesView from "../components/ExpensesView";
import ReportsView from "../components/ReportsView";
import TablesView from "../components/TablesView";
import SubscriptionView from "../components/SubscriptionView";

import { getRoleFromToken, logout } from "../auth";
import { getBusinessProfile, getSubscription } from "../api";

import { Menu as MenuIcon } from "lucide-react";

// =========================================================
// ROLE PERMISSIONS
// =========================================================

const ROLE_PERMISSIONS = {
  ADMIN: [
    "pos",
    "directPos",
    "analytics",
    "menu",
    "tables",
    "categories",
    "users",
    "chefOrders",
    "parties",
    "expenses",
    "reports",
    "profile",
    "orders",
    "orderHistory",
    "subscription",
  ],

  USER: [
    "pos",
    "directPos",
    "tables",
    "orders",
    "orderHistory",
  ],

  CHEF: [
    "chefOrders",
  ],
};

// =========================================================
// SUBSCRIPTION EXPIRY CHECK
// =========================================================

function isSubscriptionExpired(subscription) {
  if (!subscription) {
    return false;
  }

  const status = String(subscription.status || "")
    .trim()
    .toUpperCase();

  const now = Date.now();

  const parseDate = (value) => {
    if (!value) return null;

    const parsed = new Date(value).getTime();

    return Number.isNaN(parsed) ? null : parsed;
  };

  const trialEnd = parseDate(subscription.trialEnd);
  const periodEnd = parseDate(subscription.currentPeriodEnd);

  if (status === "EXPIRED") {
    return true;
  }

  if (status === "CANCELLED" || status === "PAST_DUE") {
    return true;
  }

  if (status === "TRIALING") {
    return trialEnd !== null && trialEnd <= now;
  }

  if (status === "ACTIVE") {
    return periodEnd !== null && periodEnd <= now;
  }

  return false;
}

// =========================================================
// DASHBOARD
// =========================================================

export default function Dashboard() {
  const role = String(getRoleFromToken() || "USER")
    .trim()
    .toUpperCase();

  // -------------------------------------------------------
  // INITIAL VIEW
  // -------------------------------------------------------

  const getInitialView = () => {
    if (role === "CHEF") {
      return "chefOrders";
    }

    return "pos";
  };

  const [currentView, setCurrentView] = useState(getInitialView);
  const [profile, setProfile] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Subscription expiry popup
  const [showExpiredModal, setShowExpiredModal] = useState(false);

  // -------------------------------------------------------
  // CHECK ADMIN SUBSCRIPTION
  // -------------------------------------------------------

  useEffect(() => {
    let active = true;

    // Subscription popup is only for ADMIN accounts.
    if (role !== "ADMIN") {
      setShowExpiredModal(false);

      return () => {
        active = false;
      };
    }

    getSubscription()
      .then((subscription) => {
        if (!active) return;

        setShowExpiredModal(
          isSubscriptionExpired(subscription)
        );
      })
      .catch((error) => {
        // Avoid blocking the dashboard if subscription loading fails.
        console.error(
          "Subscription expiry check failed:",
          error
        );
      });

    return () => {
      active = false;
    };
  }, [role]);

  // -------------------------------------------------------
  // PAY NOW
  // -------------------------------------------------------

  const handlePayNowFromExpiredPopup = () => {
    setShowExpiredModal(false);
    setCurrentView("subscription");
    setMobileOpen(false);
  };

  // -------------------------------------------------------
  // CHECK VIEW PERMISSIONS
  // -------------------------------------------------------

  const canAccessView = useCallback(
    (view) => {
      const permissions = ROLE_PERMISSIONS[role] || [];

      return permissions.includes(view);
    },
    [role]
  );

  // -------------------------------------------------------
  // LOAD BUSINESS PROFILE
  // -------------------------------------------------------

  useEffect(() => {
    let active = true;

    if (role === "ADMIN") {
      getBusinessProfile()
        .then((data) => {
          if (active) {
            setProfile(data || null);
          }
        })
        .catch((error) => {
          console.error(
            "Business profile load error:",
            error
          );
        });
    }

    return () => {
      active = false;
    };
  }, [role]);

  // -------------------------------------------------------
  // PROTECT CURRENT VIEW
  // -------------------------------------------------------

  useEffect(() => {
    if (!canAccessView(currentView)) {
      setCurrentView(
        role === "CHEF" ? "chefOrders" : "pos"
      );
    }
  }, [currentView, role, canAccessView]);

  // -------------------------------------------------------
  // NAVIGATION
  // -------------------------------------------------------

  const handleViewChange = useCallback(
    (view) => {
      if (!canAccessView(view)) {
        console.warn(
          `Role "${role}" cannot access view "${view}".`
        );

        return;
      }

      setCurrentView(view);
      setMobileOpen(false);
    },
    [canAccessView, role]
  );

  // -------------------------------------------------------
  // OPEN POS FROM TABLES
  // -------------------------------------------------------

  const handleOpenPOS = useCallback(
    (table, order) => {
      if (!canAccessView("pos")) {
        return;
      }

      setSelectedTable(table || null);
      setSelectedOrder(order || null);
      setCurrentView("pos");
      setMobileOpen(false);
    },
    [canAccessView]
  );

  // -------------------------------------------------------
  // RENDER CURRENT VIEW
  // -------------------------------------------------------

  const renderView = () => {
    if (!canAccessView(currentView)) {
      return role === "CHEF" ? (
        <ChefOrdersView />
      ) : (
        <POSView
          table={selectedTable}
          order={selectedOrder}
        />
      );
    }

    switch (currentView) {
      case "pos":
        return (
          <POSView
            table={selectedTable}
            order={selectedOrder}
          />
        );

      case "directPos":
        return <DirectPOSView />;

      case "menu":
        return <MenuManagement />;

      case "orders":
        return <OrdersView />;

      case "orderHistory":
        return <OrderHistory />;

      case "categories":
        return <Categories />;

      case "users":
        return <RegisterUser />;

      case "analytics":
        return <AnalyticsDashboard />;

      case "profile":
        return <Profile />;

      case "chefOrders":
        return <ChefOrdersView />;

      case "parties":
        return <PartyView />;

      case "expenses":
        return <ExpensesView />;

      case "reports":
        return <ReportsView />;

      case "subscription":
        return <SubscriptionView />;

      case "tables":
        return (
          <TablesView
            onOpenPOS={handleOpenPOS}
          />
        );

      default:
        return role === "CHEF" ? (
          <ChefOrdersView />
        ) : (
          <POSView
            table={selectedTable}
            order={selectedOrder}
          />
        );
    }
  };

  // -------------------------------------------------------
  // PAGE TITLE
  // -------------------------------------------------------

  const getTitle = () => {
    switch (currentView) {
      case "pos":
        return "POS";

      case "directPos":
        return "Direct POS";

      case "menu":
        return "Menu Management";

      case "orders":
        return "Orders";

      case "orderHistory":
        return "Order History";

      case "categories":
        return "Categories";

      case "users":
        return "Register User";

      case "analytics":
        return "Dashboard / Analytics";

      case "profile":
        return "Business Profile";

      case "chefOrders":
        return "Kitchen Orders";

      case "parties":
        return "Party Management";

      case "expenses":
        return "Expense Management";

      case "reports":
        return "Reports / Profit";

      case "tables":
        return "Restaurant Tables";

      case "subscription":
        return "Subscription";

      default:
        return "POS";
    }
  };

  // -------------------------------------------------------
  // RENDER DASHBOARD
  // -------------------------------------------------------

  return (
    <div className="so-dash-shell flex h-screen overflow-hidden">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

        .so-dash-shell {
          --ink: #1c1a17;
          --paper: #fbf9f4;
          --paper-dim: #f2ede1;
          --chili: #e4572e;
          --chili-dark: #c43f1a;
          --herb: #3f6b4f;
          --mustard: #f2a93b;
          --stone: #6b6459;
          --stone-light: #a49c8d;
          --line: #ddd4c2;
          --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
          --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          --font-mono: 'JetBrains Mono', 'Courier New', monospace;

          background: var(--paper-dim);
          font-family: var(--font-body);
          color: var(--ink);
        }

        .so-dash-shell * {
          box-sizing: border-box;
        }

        .so-dash-header {
          background: #fffdf8;
          border-bottom: 1px solid var(--line);
          box-shadow: 0 2px 0 var(--line);
        }

        .so-dash-menu-btn {
          border-radius: 6px;
          padding: 8px;
          background: transparent;
          color: var(--ink);
          transition: background 0.15s ease;
        }

        .so-dash-menu-btn:hover {
          background: var(--paper-dim);
        }

        .so-dash-logo {
          border: 1px solid var(--line);
        }

        .so-dash-eyebrow {
          font-family: var(--font-mono);
          font-size: 0.68rem;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--chili);
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .so-dash-eyebrow::before {
          content: "";
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--chili);
          display: inline-block;
        }

        .so-dash-title {
          font-family: var(--font-display);
          font-size: 1.3rem;
          letter-spacing: 0.01em;
          line-height: 1.1;
        }

        .so-dash-logout {
          background: var(--chili);
          color: var(--paper);
          font-weight: 700;
          border-radius: 6px;
          box-shadow: 0 8px 18px -8px rgba(228, 87, 46, 0.6);
          transition: background 0.15s ease, transform 0.1s ease;
        }

        .so-dash-logout:hover {
          background: var(--chili-dark);
        }

        .so-dash-logout:active {
          transform: translateY(1px);
        }

        .so-dash-main {
          background: var(--paper-dim);
          background-image: radial-gradient(circle at 1px 1px, var(--line) 1px, transparent 1.6px);
          background-size: 24px 24px;
        }
      `}</style>

      {/* SIDEBAR */}
      <Sidebar
        currentView={currentView}
        onViewChange={handleViewChange}
        role={role}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />

      {/* MAIN CONTENT */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* HEADER */}
        <header className="so-dash-header z-10 flex h-16 shrink-0 items-center justify-between px-4 md:px-6">
          <div className="flex min-w-0 items-center gap-3">
            {/* MOBILE MENU BUTTON */}
            <button
              type="button"
              className="so-dash-menu-btn md:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation menu"
            >
              <MenuIcon size={22} />
            </button>

            {/* BUSINESS LOGO */}
            {role === "ADMIN" && profile?.logoUrl && (
              <img
                src={profile.logoUrl}
                alt="Business logo"
                className="so-dash-logo hidden h-8 w-8 rounded-full object-contain sm:block"
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
            )}

            {/* BUSINESS NAME AND PAGE TITLE */}
            <div className="min-w-0">
              <h2 className="so-dash-title truncate">
                {profile?.businessName || (
                  <>
                    Swift
                    <span style={{ color: "var(--chili)" }}>
                      Order
                    </span>
                  </>
                )}
              </h2>

              <p className="so-dash-eyebrow truncate">
                {getTitle()}
              </p>
            </div>
          </div>

          {/* LOGOUT */}
          <button
            type="button"
            onClick={logout}
            className="so-dash-logout ml-3 shrink-0 px-4 py-2 text-sm"
          >
            Logout
          </button>
        </header>

        {/* PAGE CONTENT */}
        <main className="so-dash-main flex-1 overflow-auto p-3 md:p-6">
          {renderView()}
        </main>
      </div>

      {/* =====================================================
          SUBSCRIPTION EXPIRED POPUP — ADMIN ONLY
      ===================================================== */}

      {showExpiredModal && role === "ADMIN" && (
        <div
          role="presentation"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            background: "rgba(18, 16, 14, 0.68)",
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="subscription-expired-title"
            aria-describedby="subscription-expired-description"
            style={{
              width: "100%",
              maxWidth: 440,
              background: "#fffdf8",
              color: "#1c1a17",
              border: "1px solid #f3c3b0",
              borderRadius: 16,
              padding: 28,
              boxShadow: "0 24px 70px rgba(0, 0, 0, 0.28)",
              textAlign: "center",
            }}
          >
            <div
              aria-hidden="true"
              style={{
                width: 58,
                height: 58,
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 16,
                background: "#fdece6",
                color: "#c43f1a",
                fontSize: 30,
                fontWeight: 800,
              }}
            >
              !
            </div>

            <h2
              id="subscription-expired-title"
              style={{
                margin: "0 0 10px",
                fontSize: 24,
                fontWeight: 800,
              }}
            >
              Subscription Expired
            </h2>

            <p
              id="subscription-expired-description"
              style={{
                margin: "0 0 22px",
                color: "#6b6459",
                fontSize: 15,
                lineHeight: 1.6,
              }}
            >
              Your free trial or paid subscription has expired.
              Please renew your subscription to continue using
              SwiftOrder.
            </p>

            <button
              type="button"
              onClick={handlePayNowFromExpiredPopup}
              style={{
                width: "100%",
                border: 0,
                borderRadius: 8,
                padding: "13px 18px",
                background: "#e4572e",
                color: "#ffffff",
                fontSize: 15,
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              Pay Now — Renew Subscription
            </button>

            <button
              type="button"
              onClick={() => setShowExpiredModal(false)}
              style={{
                marginTop: 12,
                border: 0,
                background: "transparent",
                color: "#6b6459",
                padding: "8px 12px",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Pay Later
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

